/**
 * <LivingCreature>: a child's cut-out drawing as a canvas puppet.
 *
 *  - idle: breathing squash/stretch, sway, blink (drawn eyes get an eyelid, missing eyes get cartoon
 *    eyes that look around), occasional hop
 *  - tap: jump → wave → twirl → wiggle (sound, sparkles, haptic); pupils look at the finger
 *  - `intro`: wakes up on the paper (eyes open, stretch, happy hop)
 *  - `roam`: walks back and forth inside its box (world map)
 * The image is drawn as horizontal strips so the body can bend (core/living/puppet.ts).
 * Pauses off-screen; honours reduced motion (still blinks, no bouncing).
 */
import { memo, useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { ACTION_MS, puppetPose, rigFor, shadowScale, stripOffset, TAP_ACTIONS, type CreatureAction, type CreatureMeta, type Pose } from '@sketchling/core';
import { useApp } from '../../state/store';
import { haptic } from '../../lib/platform';
import { ambientAllowed, canvasDpr, onPowerChange } from '../../lib/power';
import { useArtStyle } from '../../art/useArtStyle';
import { STYLES } from '../../art/tokens';
import { creatureImageUrl, useCreatureStore } from './creatures';
import { ACTION_SOUND, livingSound } from './sound';
import './living.css';

export interface LivingCreatureProps {
  creature: CreatureMeta;
  /** CSS size of the box (px). Height defaults to the width. */
  size?: number;
  height?: number;
  /** Tap to play (default true). */
  interactive?: boolean;
  /** Play the wake-up intro once the image is loaded. */
  intro?: boolean;
  /** Walk back and forth inside the box (map). */
  roam?: boolean;
  /** External trigger: a new `key` plays `name`. */
  action?: { name: CreatureAction; key: number } | null;
  onAction?: (name: CreatureAction) => void;
  /** Fraction of the box height the creature may use (default 0.62; leaves room to jump). */
  scale?: number;
  /** Mute the creature's own sounds. */
  silent?: boolean;
  className?: string;
  style?: CSSProperties;
  label?: string;
  testId?: string;
}

interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  size: number;
  rot: number;
  color: string;
}

const STRIPS = 26;
const PAD = 0.07; // sprite padding (fraction of the long side) for the baked drop shadow

function prefersReduced(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

/** Bake the sticker drop shadow into a padded sprite once (deforms with the strips, no seams). */
function bakeSprite(img: HTMLImageElement): { sprite: HTMLCanvasElement; pad: number } {
  const pad = Math.round(Math.max(img.width, img.height) * PAD);
  const c = document.createElement('canvas');
  c.width = img.width + pad * 2;
  c.height = img.height + pad * 2;
  const g = c.getContext('2d')!;
  g.shadowColor = 'rgba(74, 50, 38, 0.30)';
  g.shadowBlur = pad * 0.45;
  g.shadowOffsetY = pad * 0.22;
  g.drawImage(img, pad, pad);
  return { sprite: c, pad };
}

function star(g: CanvasRenderingContext2D, x: number, y: number, r: number, rot: number) {
  g.save();
  g.translate(x, y);
  g.rotate(rot);
  g.beginPath();
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const rr = i % 2 ? r * 0.36 : r;
    g.lineTo(Math.cos(a) * rr, Math.sin(a) * rr);
  }
  g.closePath();
  g.fill();
  g.restore();
}

export const LivingCreature = memo(function LivingCreature({
  creature,
  size = 220,
  height,
  interactive = true,
  intro = false,
  roam = false,
  action,
  onAction,
  scale = 0.62,
  silent = false,
  className = '',
  style,
  label,
  testId = 'living-creature',
}: LivingCreatureProps) {
  const W = size;
  const H = height ?? size;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);
  const spriteRef = useRef<{ sprite: HTMLCanvasElement; pad: number; iw: number; ih: number } | null>(null);
  const actRef = useRef<{ name: CreatureAction; start: number } | null>(null);
  const t0 = useRef(typeof performance !== 'undefined' ? performance.now() : 0);
  const sparks = useRef<Spark[]>([]);
  const tapIdx = useRef(0);
  const lookAt = useRef<{ x: number; y: number; until: number } | null>(null);
  const visible = useRef(true);
  const kick = useRef<() => void>(() => undefined);
  const userReduced = useApp((s) => s.settings.reducedMotion);
  const styleId = useArtStyle();
  const inkColor = STYLES[styleId].line;
  const reduced = userReduced || prefersReduced();
  const rig = useRef(rigFor(creature.id, creature.kind));
  rig.current = rigFor(creature.id, creature.kind);
  const silentRef = useRef(silent);
  silentRef.current = silent;
  const onActionRef = useRef(onAction);
  onActionRef.current = onAction;
  const meRef = useRef(creature);
  meRef.current = creature;
  const imageKey = creature.imageKey;

  // load the PNG → baked sprite
  useEffect(() => {
    let alive = true;
    setReady(false);
    void creatureImageUrl({ imageKey }).then((url) => {
      if (!url || !alive) return;
      const img = new Image();
      img.onload = () => {
        if (!alive) return;
        const { sprite, pad } = bakeSprite(img);
        spriteRef.current = { sprite, pad, iw: img.width, ih: img.height };
        if (intro) actRef.current = { name: 'intro', start: performance.now() - t0.current };
        kick.current();
        setReady(true);
      };
      img.src = url;
    });
    return () => {
      alive = false;
    };
  }, [imageKey, intro]);

  const burst = useCallback(
    (n: number, x?: number, y?: number) => {
      const colors = [creature.accent, '#FFD86B', '#FFFBF3', '#F4A3B5'];
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2;
        const sp = 1.2 + Math.random() * 2.6;
        sparks.current.push({
          x: x ?? W / 2 + (Math.random() - 0.5) * W * 0.3,
          y: y ?? H * 0.5 + (Math.random() - 0.5) * H * 0.3,
          vx: Math.cos(a) * sp,
          vy: Math.sin(a) * sp - 1.4,
          life: 1,
          size: 3 + Math.random() * 5,
          rot: Math.random() * 3,
          color: colors[i % colors.length]!,
        });
      }
    },
    [creature.accent, W, H],
  );

  const play = useCallback(
    (name: CreatureAction, at?: { x: number; y: number }) => {
      actRef.current = { name, start: performance.now() - t0.current };
      kick.current();
      if (!silentRef.current) livingSound(ACTION_SOUND[name], 0.9 + (rig.current.seed % 5) * 0.06);
      burst(name === 'intro' ? 18 : 10, at?.x, at?.y);
      onActionRef.current?.(name);
    },
    [burst],
  );

  // external trigger
  const lastKey = useRef<number | null>(null);
  useEffect(() => {
    if (!action || !ready || lastKey.current === action.key) return;
    lastKey.current = action.key;
    play(action.name);
  }, [action, ready, play]);

  // intro sound + sparkles at landing
  useEffect(() => {
    if (!ready || !intro) return;
    if (!silentRef.current) livingSound('wake');
    const id = setTimeout(() => (burst(20), silentRef.current ? undefined : livingSound('shimmer')), ACTION_MS.intro * 0.86);
    return () => clearTimeout(id);
  }, [ready, intro, burst]);

  // pause when off-screen
  useEffect(() => {
    const el = canvasRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((es) => {
      visible.current = es.some((e) => e.isIntersecting);
      if (visible.current) kick.current();
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // render loop
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const g = c.getContext('2d');
    if (!g) return;
    const dpr = canvasDpr();
    c.width = Math.round(W * dpr);
    c.height = Math.round(H * dpr);
    let raf = 0;
    let facing = 1;
    let lastTx = 0;
    let last = 0;
    let drawn = false;
    // The loop runs only while the canvas is on screen and the app visible; decorative motion is capped at 30 fps
    // and stops (one still frame) in battery saver. IntersectionObserver / power changes restart it.
    const frame = (now: number) => {
      raf = 0;
      if (!visible.current || document.hidden) return;
      const still = !ambientAllowed() && !actRef.current && !sparks.current.length && drawn;
      if (still) return;
      raf = requestAnimationFrame(frame);
      if (now - last < 31 && drawn) return;
      last = now;
      drawn = true;
      const sp = spriteRef.current;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, W, H);
      if (!sp) return;
      const t = performance.now() - t0.current;
      const act = actRef.current;
      if (act && t - act.start > ACTION_MS[act.name]) actRef.current = null;
      const pose: Pose = puppetPose(rig.current, t, actRef.current, reduced);
      // box geometry
      const aspect = sp.iw / sp.ih;
      let bh = H * scale;
      let bw = bh * aspect;
      const maxW = W * (roam ? 0.5 : 0.8);
      if (bw > maxW) {
        bw = maxW;
        bh = bw / aspect;
      }
      const ax = W / 2;
      const ay = H * 0.9;
      // roaming: slow walk with little hop-steps
      let rx = 0;
      if (roam && !reduced) {
        const span = Math.max(0, (W - bw) / 2 - 4);
        rx = Math.sin(t / 5200 + (rig.current.seed % 17)) * span;
        const dx = rx - lastTx;
        if (Math.abs(dx) > 0.05) facing = dx > 0 ? 1 : -1;
        lastTx = rx;
        const step = (t / 210) % 1;
        pose.ty += Math.abs(Math.sin(step * Math.PI)) * 0.045;
        pose.rot += Math.sin(step * Math.PI * 2) * 0.03;
        pose.shuffle += Math.sin(step * Math.PI * 2) * 0.04;
        pose.look.x = facing * 0.8;
      }
      const la = lookAt.current;
      if (la && t < la.until) {
        pose.look.x = Math.max(-1, Math.min(1, (la.x - ax) / (bw * 0.6)));
        pose.look.y = Math.max(-1, Math.min(1, (la.y - (ay - bh * 0.6)) / (bh * 0.6)));
      }
      const cx = ax + rx + pose.tx * bh;
      const lift = pose.ty * bh;
      // ground shadow
      const ss = shadowScale(pose.ty);
      g.fillStyle = `rgba(74, 50, 38, ${0.2 * ss})`;
      g.beginPath();
      g.ellipse(ax + rx + pose.tx * bh, ay + 1, bw * 0.4 * ss, Math.max(2, bh * 0.055 * ss), 0, 0, Math.PI * 2);
      g.fill();

      g.save();
      g.translate(cx, ay - lift);
      g.rotate(pose.rot);
      g.scale(pose.sx * (roam ? facing : 1), pose.sy);
      // strips (sprite = padded image with its baked shadow)
      const k = bw / sp.iw;
      const pad = sp.pad;
      const rows = sp.ih + pad * 2;
      const top = -bh - pad * k;
      const left = -bw / 2 - pad * k;
      for (let i = 0; i < STRIPS; i++) {
        const r0 = (i / STRIPS) * rows;
        const r1 = ((i + 1) / STRIPS) * rows;
        const v = Math.max(0, Math.min(1, ((r0 + r1) / 2 - pad) / sp.ih));
        const dx = stripOffset(pose, v) * bw;
        g.drawImage(sp.sprite, 0, r0, sp.sprite.width, r1 - r0 + 0.5, left + dx, top + r0 * k, sp.sprite.width * k, (r1 - r0) * k + 0.6);
      }
      // eyes
      const creature = meRef.current;
      for (const e of creature.eyes) {
        const ex = -bw / 2 + e.x * bw + stripOffset(pose, e.y) * bw;
        const ey = -bh + e.y * bh;
        const r = Math.max(2.2, e.r * bw);
        drawEye(g, ex, ey, r, pose, creature.eyesDrawn, e.lid, inkColor, roam ? facing : 1);
      }
      if (!creature.eyesDrawn && creature.eyes.length === 2) {
        // rosy cheeks under added eyes
        const [l, rr] = creature.eyes as [typeof creature.eyes[0], typeof creature.eyes[0]];
        const r = Math.max(2.2, l.r * bw);
        g.fillStyle = 'rgba(240, 138, 138, 0.42)';
        for (const [e, side] of [
          [l, -1],
          [rr, 1],
        ] as const) {
          const ex = -bw / 2 + e.x * bw + stripOffset(pose, e.y) * bw + side * r * 0.55;
          const ey = -bh + e.y * bh + r * 1.55;
          g.beginPath();
          g.ellipse(ex, ey, r * 0.62, r * 0.36, 0, 0, Math.PI * 2);
          g.fill();
        }
      }
      g.restore();

      // sparkles
      const ps = sparks.current;
      for (let i = ps.length - 1; i >= 0; i--) {
        const p = ps[i]!;
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.06;
        p.rot += 0.08;
        p.life -= 0.018;
        if (p.life <= 0) {
          ps.splice(i, 1);
          continue;
        }
        g.globalAlpha = Math.min(1, p.life * 1.6);
        g.fillStyle = p.color;
        star(g, p.x, p.y, p.size * (0.6 + p.life * 0.4), p.rot);
      }
      g.globalAlpha = 1;
    };
    kick.current = () => {
      if (!raf) raf = requestAnimationFrame(frame);
    };
    kick.current();
    const off = onPowerChange(() => kick.current());
    return () => {
      cancelAnimationFrame(raf);
      off();
      kick.current = () => undefined;
    };
  }, [W, H, reduced, roam, scale, inkColor]);

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!interactive || !ready) return;
    const box = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - box.left) / box.width) * W;
    const y = ((e.clientY - box.top) / box.height) * H;
    const t = performance.now() - t0.current;
    lookAt.current = { x, y, until: t + 1400 };
    const name = TAP_ACTIONS[tapIdx.current++ % TAP_ACTIONS.length]!;
    play(name, { x, y });
    haptic('light');
    const st = useCreatureStore.getState();
    const cur = st.creatures.find((c) => c.id === creature.id);
    if (cur) st.updateCreature(cur.id, { plays: (cur.plays ?? 0) + 1 });
  };

  return (
    <canvas
      ref={canvasRef}
      className={`living-creature${interactive ? ' tappable' : ''} ${className}`}
      style={{ width: W, height: H, ...style }}
      data-testid={testId}
      data-creature-id={creature.id}
      data-state={ready ? 'alive' : 'loading'}
      role={interactive ? 'button' : 'img'}
      aria-label={label ?? creature.name}
      onPointerDown={onPointerDown}
    />
  );
});

function drawEye(g: CanvasRenderingContext2D, x: number, y: number, r: number, pose: Pose, drawn: boolean, lid: string | undefined, ink: string, facing: number) {
  const happy = pose.happy > 0.5;
  const open = 1 - pose.blink;
  g.lineCap = 'round';
  g.lineJoin = 'round';
  if (drawn) {
    // the child's own eye: only cover it when closed / happy
    if (!happy && open > 0.55) return;
    g.fillStyle = lid ?? '#FFFBF3';
    g.beginPath();
    g.ellipse(x, y, r * 1.35, r * 1.35, 0, 0, Math.PI * 2);
    g.fill();
    g.strokeStyle = ink;
    g.lineWidth = Math.max(1.4, r * 0.38);
    g.beginPath();
    if (happy) {
      g.moveTo(x - r * 0.9, y + r * 0.3);
      g.quadraticCurveTo(x, y - r * 0.9, x + r * 0.9, y + r * 0.3);
    } else {
      g.moveTo(x - r * 0.95, y);
      g.quadraticCurveTo(x, y + r * 0.7, x + r * 0.95, y);
    }
    g.stroke();
    return;
  }
  // cartoon eye
  g.strokeStyle = ink;
  g.lineWidth = Math.max(1.3, r * 0.17);
  if (happy) {
    g.lineWidth = Math.max(1.6, r * 0.3);
    g.beginPath();
    g.moveTo(x - r * 0.85, y + r * 0.25);
    g.quadraticCurveTo(x, y - r * 0.85, x + r * 0.85, y + r * 0.25);
    g.stroke();
    return;
  }
  const ry = r * 1.1 * Math.max(0.08, open);
  g.fillStyle = '#FFFFFF';
  g.beginPath();
  g.ellipse(x, y, r, ry, 0, 0, Math.PI * 2);
  g.fill();
  g.stroke();
  if (open < 0.3) return;
  g.save();
  g.beginPath();
  g.ellipse(x, y, r, ry, 0, 0, Math.PI * 2);
  g.clip();
  const px = x + pose.look.x * r * 0.32 * facing;
  const py = y + pose.look.y * r * 0.28 + r * 0.08;
  g.fillStyle = '#3B2A25';
  g.beginPath();
  g.arc(px, py, r * 0.6, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = '#FFFFFF';
  g.beginPath();
  g.arc(px + r * 0.22, py - r * 0.24, r * 0.22, 0, Math.PI * 2);
  g.fill();
  g.beginPath();
  g.arc(px - r * 0.2, py + r * 0.2, r * 0.1, 0, Math.PI * 2);
  g.fill();
  g.restore();
}
